import { ContentType, CONTENTTYPE_PERMISSIONS_MAP } from '@creditsights/api-ui-web';
import {
  ContentTypeTag,
  colors,
  DataView,
  DataViewConfig,
  Icon,
  ICONS,
  Tooltip,
  Widget,
  SortableWidgetProps,
  Pagination,
  Loader,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef } from 'react';
import ResultsFilter from '../../../components/ResultsFilter/ResultsFilter';
import { useLocalStorage } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import { DisplayType } from '../FeedLeftContainer/DisplayTypeSelection';
import { FEED_SECTION_TITLES, getSectionHeaderId } from '../FeedLeftContainer/FeedLeftContainer.logic';
import styles from './ArticlesFeedPaginatedWidget.module.scss';

const cx = classNames.bind(styles);

export interface ArticlesFeedPaginatedProps extends SortableWidgetProps {
  viewConfig: DataViewConfig<ArticleTableRow>;
  isLoading: boolean;
  isError: boolean;
  onRetryAfterErrorClick: () => void;
  contentType: ContentType;
  showItemsCount: number;
  onShowItemsCountChange: (count: number) => void;
  showAllLoadItemCount?: number;
  filterEnabled: boolean;
  onShowAllSelected?: () => void;
  displayType: DisplayType;
  paginationOptions: number[];
  articles?: ArticleTableRow[];
  widgetHeader?: JSX.Element;
  collapsable?: boolean;
  previewArticleId?: number[];
  isAlwaysExpanded?: boolean;
  page: number;
  fetchPage: (page: number) => void;
  totalPages: number;
  isTableCategoryTabActive: boolean;
}

const getKey = (article: ArticleTableRow) => article.id;

const ROW_HEIGHT = 41;
const PAGINATION_HEIGHT = 45;

const ArticlesFeedPaginatedWidget = (
  {
    viewConfig,
    isLoading,
    isError,
    onRetryAfterErrorClick,
    contentType,
    showItemsCount,
    onShowItemsCountChange,
    showAllLoadItemCount,
    filterEnabled,
    onShowAllSelected,
    displayType,
    paginationOptions,
    articles,
    widgetHeader,
    collapsable = true,
    previewArticleId,
    isAlwaysExpanded,
    page,
    fetchPage,
    totalPages,
    isTableCategoryTabActive,
    ...sortableWidgetProps
  }: ArticlesFeedPaginatedProps,
  ref
): React.ReactElement | null => {
  const noData = !isLoading && !articles?.length;
  const isCollapsable = collapsable && (!isLoading || !!articles?.length || isError);
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>(`is${contentType}ArticlesFeedWidgetExpanded`, true);
  const { data: userPermissions } = useFetchUserPermissions();
  const hasPermissions = userPermissions!.permissions.includes(CONTENTTYPE_PERMISSIONS_MAP[contentType]);
  const { isDragged } = sortableWidgetProps;
  const loaderHeight = isTableCategoryTabActive ? '60vh' : `${showItemsCount * ROW_HEIGHT + PAGINATION_HEIGHT}px`;

  const renderTable = () => {
    if (noData) {
      return undefined;
    }

    return (
      <DataView
        data={articles || []}
        config={viewConfig}
        getKey={getKey}
        expanded={displayType === DisplayType.Expanded}
        activeKeys={previewArticleId}
      />
    );
  };

  const renderResultsFilter = () => (
    <ResultsFilter
      selectedValue={showItemsCount}
      values={paginationOptions}
      onResultsCountChange={onShowItemsCountChange}
      optionTestIdPrefix="article-count-option"
      onShowAllSelected={onShowAllSelected}
      showAllLoadItemCount={showAllLoadItemCount}
    />
  );

  const renderFooter = () => {
    if (isLoading || !page || !totalPages || totalPages <= 1) {
      return null;
    }

    return (
      <div className={cx('footer')} data-testid="articles-feed-footer">
        <Pagination activePage={page} onPageChange={fetchPage} totalPages={totalPages} />
      </div>
    );
  };

  const renderHeader = () => (
    <div className={cx('header-content', `${contentType.toLowerCase()}`)} id={getSectionHeaderId(contentType)}>
      {FEED_SECTION_TITLES[contentType]}
      {!isDragged && (
        <>
          {!hasPermissions && (
            <Icon dataTestId="content-lock-icon" name={ICONS.LockOutline} color={colors.tertiary800} />
          )}
          <span aria-hidden="true">
            <ContentTypeTag contentType={contentType} />
          </span>
          {!articles?.length && !isLoading && (
            <Tooltip
              trigger={
                <span className={cx('info-icon')}>
                  <Icon name={ICONS.Info} dataTestId={`info-icon-${contentType}`} />
                </span>
              }
              content={filterEnabled ? 'No results' : 'Update your content filters'}
            />
          )}
        </>
      )}
    </div>
  );

  const renderLoader = () => (
    <div style={{ height: loaderHeight }}>
      <Loader position="center" />
    </div>
  );

  return (
    <Widget
      classNames={cx('body', { 'no-data': (!filterEnabled || noData) && !isError })}
      headerTitle={widgetHeader || renderHeader()}
      collapsable={isCollapsable && !isDragged}
      isExpanded={isAlwaysExpanded || isExpanded}
      setIsExpanded={setIsExpanded}
      firstLine={renderResultsFilter()}
      isError={isError}
      onRetryAfterErrorClick={onRetryAfterErrorClick}
      footer={renderFooter()}
      showFooterDivider
      dataTestId={`articles-feed-${contentType}`}
      fixedHeight
      ref={ref}
      {...sortableWidgetProps}
    >
      {isLoading ? renderLoader() : renderTable()}
    </Widget>
  );
};

export default forwardRef(ArticlesFeedPaginatedWidget);
